import { useState } from 'react';
import { Link } from 'react-router';
import { daysUntil, whatsappNumber } from '@pos/core';
import { useSession } from '../../app/current.tsx';
import { env } from '../../lib/env.ts';
import { refreshLicense, useLicenseState } from '../../services/license.ts';
import { Button, Card, cx } from '../../ui/index.tsx';
import { FullExportButton } from '../reports/ReportsPage.tsx';
import { PayWithMp } from './PayWithMp.tsx';

const dateFormat = new Intl.DateTimeFormat('es-AR', { dateStyle: 'long' });

function supportLink(text: string): string | null {
  const number = whatsappNumber(env.supportWhatsapp);
  return number ? `https://wa.me/${number}?text=${encodeURIComponent(text)}` : null;
}

/** Aviso arriba de todas las pantallas: prueba por terminar, vencida (gracia) o modo consulta. */
export function LicenseBanner() {
  const { business, isOwner } = useSession();
  const state = useLicenseState();
  if (state.kind !== 'ok') return null;
  const { status, schedule } = state;
  const left = daysUntil(schedule.activeUntil);

  let text: string | null = null;
  let tone = 'bg-amber-50 text-amber-900 ring-amber-200';
  if (status === 'activa' && schedule.trial && left <= 5) {
    text = left === 0 ? 'Hoy termina la prueba gratis.' : `Te quedan ${left} ${left === 1 ? 'día' : 'días'} de prueba gratis.`;
    tone = 'bg-sky-50 text-sky-900 ring-sky-200';
  } else if (status === 'en_gracia') {
    text =
      schedule.override || !schedule.graceUntil
        ? 'La suscripción está vencida. Podés seguir usando todo por ahora; regularizala para no pasar a modo consulta.'
        : `La suscripción venció. Podés seguir usando todo hasta el ${dateFormat.format(schedule.graceUntil)}; después queda en modo consulta.`;
  } else if (status === 'solo_lectura') {
    text =
      schedule.override || !schedule.readonlyUntil
        ? 'Modo consulta: podés ver todo, pero no cargar ventas ni cambios.'
        : `Modo consulta: podés ver todo, pero no cargar ventas ni cambios. El ${dateFormat.format(schedule.readonlyUntil)} se suspende.`;
    tone = 'bg-red-50 text-red-900 ring-red-200';
  }
  if (!text) return null;
  const link = isOwner ? supportLink(`Hola, quiero regularizar la suscripción de ${business.name}.`) : null;

  return (
    <div className={cx('mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg p-3 text-sm ring-1', tone)}>
      <span>
        {text}
        {!isOwner && status !== 'activa' && ' Avisale al dueño.'}
      </span>
      {isOwner && (
        <span className="flex flex-wrap gap-3">
          <Link to="/configuracion" className="font-semibold underline">
            Pagar con Mercado Pago
          </Link>
          {link && (
            <a href={link} target="_blank" rel="noopener" className="font-semibold underline">
              WhatsApp
            </a>
          )}
        </span>
      )}
    </div>
  );
}

/** Pantalla completa: suscripción suspendida, hace mucho que no se verifica o reloj atrasado. */
export function LicenseBlock() {
  const { business, device, isOwner } = useSession();
  const state = useLicenseState();
  const [checking, setChecking] = useState(false);
  const stale = state.kind === 'stale';
  const badClock = state.kind === 'clock';
  const link = supportLink(`Hola, la suscripción de ${business.name} está suspendida. Quiero regularizarla.`);

  return (
    <Card className="mx-auto max-w-lg space-y-4 text-center">
      <h1 className="text-xl font-semibold">{badClock ? 'Revisá la fecha y hora' : stale ? 'Conectate a internet' : 'Suscripción suspendida'}</h1>
      <p className="text-sm text-slate-600">
        {badClock
          ? 'La fecha u hora de este dispositivo está atrasada. Corregila en la configuración del dispositivo (mejor en automático) o conectalo a internet y tocá «Verificar de nuevo».'
          : stale
          ? 'Hace muchos días que este dispositivo no se conecta y no se puede verificar la suscripción. Conectalo a internet y tocá «Verificar».'
          : isOwner
            ? 'Tus datos están guardados y no se pierden. Para volver a usar el sistema, regularizá la suscripción.'
            : 'La suscripción del negocio está suspendida. Avisale al dueño.'}
      </p>
      <div className="flex flex-wrap justify-center gap-2">
        {!stale && !badClock && isOwner && link && (
          <a href={link} target="_blank" rel="noopener">
            <Button>Escribir por WhatsApp</Button>
          </a>
        )}
        <Button
          variant="secondary"
          loading={checking}
          onClick={async () => {
            setChecking(true);
            try {
              await refreshLicense(device.id);
            } finally {
              setChecking(false);
            }
          }}
        >
          Verificar de nuevo
        </Button>
      </div>
      {isOwner && !stale && !badClock && (
        <>
          <PayWithMp planCode={state.kind === 'ok' ? state.license.plan.code : null} />
          <div className="border-t border-slate-100 pt-4 text-left">
            <FullExportButton />
          </div>
        </>
      )}
    </Card>
  );
}

/** Para empleados con la suscripción en modo consulta: en Vender y Ventas no se ve nada. */
export function ReadOnlyNotice() {
  return (
    <Card className="mx-auto max-w-lg space-y-2 text-center">
      <h1 className="text-xl font-semibold">Modo consulta</h1>
      <p className="text-sm text-slate-600">La suscripción del negocio no permite vender por ahora. Avisale al dueño.</p>
    </Card>
  );
}

export function useLicenseBlocked(): boolean {
  const state = useLicenseState();
  return state.kind === 'stale' || state.kind === 'clock' || (state.kind === 'ok' && state.status === 'suspendida');
}
